import React, { useCallback, useRef } from 'react';
import { Dispatch } from 'redux';
import { Popper } from 'react-popper';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { RootAction, State } from 'store/state';
import { setPrivateBulletinSuccesfullyPromotedPopover, promotePrivateBulletin, PromotePrivateBulletinViewSource, PromotePrivateBulletinPayload } from 'store/state/app/actions';
import { createNextAvailablePromotionDateSelector, currentPromotionPlanIntervalHoursSelector, isBasicPromotionSelector, isPrivateBulletinSuccesfullyPromotedPopoverSelector, isPromotePrivateBulletinMutationLoadingSelector } from 'store/state/app/private-user-monetization/selectors';
import { BulletinPoiType, DealType } from 'utils/entities';
import { ManualPromoteBulletinButton, ManualPromoteBulletinButtonProps } from './ManualPromoteBulletinButton';
import { Autohide } from '../Autohide';
import { SuccessPopoverMessage } from './PopoverMessage';
import { SuccessMessage } from './Message';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';

type SmartManualPromoteBulletinButtonProps = Omit<ManualPromoteBulletinButtonProps, 'onClick'> & {
  bulletinId: string;
  bulletinType: BulletinPoiType;
  dealType: DealType;
  viewSource: PromotePrivateBulletinViewSource;
  promoteBulletin: (payload: PromotePrivateBulletinPayload) => void;
  isPromoteBulletinLoading: boolean;
  successPopoverState: {
    isOpen: boolean;
    isPaidAndRunning?: boolean;
  };
  onCloseSuccessPopover: () => void;
  isFreePlan?: boolean;
  hoursInterval?: number;
};

const SmartManualPromoteBulletinButton: React.FC<SmartManualPromoteBulletinButtonProps> = props => {
  const {
    promotedUntil,
    mode,
    size,
    className,
    successPopoverState,
    onCloseSuccessPopover,
    bulletinId,
    bulletinType,
    viewSource,
    dealType,
    promoteBulletin,
    isPromoteBulletinLoading,
    fullWidth,
    isFreePlan,
    hoursInterval,
  } = props;
  const { t } = useLocale();
  const buttonRef = useRef(null);
  const { route } = useRoute();

  const handlePromoteClick = useCallback((event: React.MouseEvent, data: { timeLeft: number }) => {
    promoteBulletin({
      bulletinId,
      bulletinType,
      dealType,
      viewSource,
      isTimerStillRunning: data.timeLeft > 0,
    });
  }, [ promoteBulletin, bulletinId, bulletinType, viewSource ]);

  return route.name === Route.ManageBulletins && isFreePlan ? (
    <>
      <ManualPromoteBulletinButton
        ref={buttonRef}
        className={className}
        mode={mode}
        size={size}
        promotedUntil={promotedUntil}
        onClick={handlePromoteClick}
        disabled={isPromoteBulletinLoading}
        fullWidth={fullWidth}
      />
      <Autohide
        show={successPopoverState.isOpen}
        seconds={10}
        onClose={onCloseSuccessPopover}
      >
        <SuccessMessage onClose={onCloseSuccessPopover} />
      </Autohide>
    </>
  ) : (
    <>
      <ManualPromoteBulletinButton
        ref={buttonRef}
        className={className}
        mode={mode}
        size={size}
        promotedUntil={promotedUntil}
        onClick={handlePromoteClick}
        disabled={isPromoteBulletinLoading}
        fullWidth={fullWidth}
      />
      <Popper
        referenceElement={buttonRef.current}
        placement="top"
      >
        {({ ref, style }) => (
          <Autohide
            show={successPopoverState.isOpen}
            seconds={5}
            onClose={onCloseSuccessPopover}
          >
            <SuccessPopoverMessage
              ref={ref}
              style={{ ...style, top: -4 }}
              isPaidAndRunning={successPopoverState.isPaidAndRunning}
              onClose={onCloseSuccessPopover}
              message={successPopoverState.isPaidAndRunning ? t('privateBulletinPromotion.successfullyPromotedPaidAndRunning', { hours: hoursInterval }) : t('privateBulletinPromotion.successfullyPromoted')}
            />
          </Autohide>
        )}
      </Popper>
    </>
  );
};

const mapStateToProps = (state: State, ownProps: { bulletinId: string }) => ({
  promotedUntil: createNextAvailablePromotionDateSelector(ownProps.bulletinId)(state),
  successPopoverState: isPrivateBulletinSuccesfullyPromotedPopoverSelector(state),
  isPromoteBulletinLoading: isPromotePrivateBulletinMutationLoadingSelector(state),
  isFreePlan: isBasicPromotionSelector(state),
  hoursInterval: currentPromotionPlanIntervalHoursSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  promoteBulletin: (payload: PromotePrivateBulletinPayload) => dispatch(promotePrivateBulletin(payload)),
  onCloseSuccessPopover: () => dispatch(setPrivateBulletinSuccesfullyPromotedPopover({ isOpen: false })),
});

export default connect(mapStateToProps, mapDispatchToProps)(SmartManualPromoteBulletinButton);
